import React, { useMemo } from "react";
import { FileSpreadsheet, FileText } from "lucide-react";
import { exportCreditUsageToExcel, exportCreditUsageToPDF } from "../lib/exportUtils";

const WhatsappIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 256 258">
    <defs>
      <linearGradient id="whatsappGradient1" x1="50%" x2="50%" y1="100%" y2="0%">
        <stop offset="0%" stopColor="#1faf38" />
        <stop offset="100%" stopColor="#60d669" />
      </linearGradient>
      <linearGradient id="whatsappGradient2" x1="50%" x2="50%" y1="100%" y2="0%">
        <stop offset="0%" stopColor="#f9f9f9" />
        <stop offset="100%" stopColor="#fff" />
      </linearGradient>
    </defs>
    <path
      fill="url(#whatsappGradient1)"
      d="M5.463 127.456c-.006 21.677 5.658 42.843 16.428 61.499L4.433 252.697l65.232-17.104a123 123 0 0 0 58.8 14.97h.054c67.815 0 123.018-55.183 123.047-123.01c.013-32.867-12.775-63.773-36.009-87.025c-23.23-23.25-54.125-36.061-87.043-36.076c-67.823 0-123.022 55.18-123.05 123.004"
    />
    <path
      fill="url(#whatsappGradient2)"
      d="M1.07 127.416c-.007 22.457 5.86 44.38 17.014 63.704L0 257.147l67.571-17.717c18.618 10.151 39.58 15.503 60.91 15.511h.055c70.248 0 127.434-57.168 127.464-127.423c.012-34.048-13.236-66.065-37.3-90.15C194.633 13.286 162.633.014 128.536 0C58.276 0 1.099 57.16 1.071 127.416m40.24 60.376l-2.523-4.005c-10.606-16.864-16.204-36.352-16.196-56.363C22.614 69.029 70.138 21.52 128.576 21.52c28.3.012 54.896 11.044 74.9 31.06c20.003 20.018 31.01 46.628 31.003 74.93c-.026 58.395-47.551 105.91-105.943 105.91h-.042c-19.013-.01-37.66-5.116-53.922-14.765l-3.87-2.295l-40.098 10.513z"
    />
    <path
      fill="#fff"
      d="M96.678 74.148c-2.386-5.303-4.897-5.41-7.166-5.503c-1.858-.08-3.982-.074-6.104-.074c-2.124 0-5.575.799-8.492 3.984c-2.92 3.188-11.148 10.892-11.148 26.561s11.413 30.813 13.004 32.94c1.593 2.123 22.033 35.307 54.405 48.073c26.904 10.609 32.379 8.499 38.218 7.967c5.84-.53 18.844-7.702 21.497-15.139c2.655-7.436 2.655-13.81 1.859-15.142c-.796-1.327-2.92-2.124-6.105-3.716s-18.844-9.298-21.763-10.361c-2.92-1.062-5.043-1.592-7.167 1.597c-2.124 3.184-8.223 10.356-10.082 12.48c-1.857 2.129-3.716 2.394-6.9.801c-3.187-1.598-13.444-4.957-25.613-15.806c-9.468-8.442-15.86-18.867-17.718-22.056c-1.858-3.184-.199-4.91 1.398-6.497c1.431-1.427 3.186-3.719 4.78-5.578c1.588-1.86 2.118-3.187 3.18-5.311c1.063-2.126.531-3.986-.264-5.579c-.798-1.593-6.987-17.343-9.819-23.64"
    />
  </svg>
);

const EmailIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 32 32">
    <path
      fill="#42a5f5"
      d="M28 6H4a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h24a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2m0 6l-12 6l-12-6V8l12 6l12-6Z"
    />
  </svg>
);

const CreditBillingTable = ({ creditUsage = [] }) => {
  const totals = useMemo(() => {
    return creditUsage.reduce(
      (acc, usage) => {
        acc.totalMessages += usage.usedMessages ?? 0;
        acc.totalCredits += usage.totalCredit ?? 0;
        acc.revenue += usage.revenue ?? 0;
        acc.overUsedMessages += usage.overUsedMessages ?? 0;
        return acc;
      },
      { totalMessages: 0, totalCredits: 0, overUsedMessages: 0, revenue: 0 },
    );
  }, [creditUsage]);

  const groupedUsage = useMemo(() => {
    const map = new Map();
    creditUsage.forEach((usage) => {
      const key = usage.clinicId || usage.clinicName;
      if (!map.has(key)) {
        map.set(key, {
          clinicId: usage.clinicId,
          clinicName: usage.clinicName || "—",
          channels: [],
        });
      }
      map.get(key)?.channels.push(usage);
    });
    return Array.from(map.values());
  }, [creditUsage]);

  return (
    <div className="pharmacy-panel">
      <div className="pharmacy-panel-header">
        <div>
          <h2 className="pharmacy-panel-title">Clinic Channel Credits</h2>
          <p className="pharmacy-page-subtitle">
            Per-channel credit usage, remaining balances, and revenue
          </p>
        </div>
        <div className="pharmacy-action-group">
          <button
            type="button"
            className="pharmacy-button"
            onClick={() => exportCreditUsageToExcel(creditUsage)}
          >
            <FileSpreadsheet className="pharmacy-button-icon" />
            Export Excel
          </button>
          <button
            type="button"
            className="pharmacy-button"
            onClick={() => exportCreditUsageToPDF(creditUsage)}
          >
            <FileText className="pharmacy-button-icon" />
            Export PDF
          </button>
        </div>
      </div>

      <div className="pharmacy-table-wrapper">
        <table className="pharmacy-table">
          <thead>
            <tr>
              <th rowSpan={2}>Clinic</th>
              <th rowSpan={2}></th>
              <th className="text-center" colSpan={3}>
                Credits
              </th>
              <th
                className="text-center"
                colSpan={2}
                style={{ borderLeft: "1px solid #e5e7eb" }}
              >
                Messages
              </th>
              <th rowSpan={2} className="text-right">
                Revenue
              </th>
            </tr>
            <tr>
              <th className="text-right">Total </th>
              <th className="text-right">Used</th>
              <th className="text-right">Remaining</th>
              <th
                className="text-right"
                style={{ borderLeft: "1px solid #e5e7eb" }}
              >
                Used{" "}
              </th>
              <th className="text-right">Overused</th>
            </tr>
          </thead>
          <tbody>
            {groupedUsage.length === 0 ? (
              <tr>
                <td colSpan={8} style={{ textAlign: "center", padding: "32px 16px" }}>
                  No credit usage found.
                </td>
              </tr>
            ) : (
              groupedUsage.map((group) =>
                group.channels.map((usage, index) => (
                  <tr key={`${group.clinicId}-${usage.channelType}-${index}`}>
                    {index === 0 && (
                      <td rowSpan={group.channels.length} className="clinic-merge-cell">
                        {group.clinicName}
                      </td>
                    )}
                    <td>
                      <span className="channel-pill">
                        {usage.channelType === "whatsapp" ? <WhatsappIcon /> : <EmailIcon />}
                        {/* <span>
                          {usage.channelType === "whatsapp" ? "WhatsApp" : "Email"}
                        </span> */}
                      </span>
                    </td>
                    <td className="text-center">
                      {usage.totalCredit?.toLocaleString() ?? "0"}
                    </td>
                    <td className="text-center">
                      {usage.usedCredits?.toLocaleString() ?? "0"}
                    </td>
                    <td className="text-center">
                      {usage.remainingCredits?.toLocaleString() ?? "0"}
                    </td>
                    <td className="text-center">
                      {usage.usedMessages?.toLocaleString() ?? "0"}
                    </td>
                    <td className="text-center">
                      {usage.overUsedMessages?.toLocaleString() ?? "0"}
                    </td>
                    <td className="text-right text-primary">
                      QAR {Number(usage.revenue ?? 0).toLocaleString()}
                    </td>
                  </tr>
                )),
              )
            )}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={5}>
                <strong>Totals</strong>
              </td>
              <td className="text-right">
                <strong>{totals.totalMessages.toLocaleString()}</strong>
              </td>
              <td className="text-right">
                <strong>{totals.overUsedMessages.toLocaleString()}</strong>
              </td>
              <td className="text-right text-primary">
                <strong>QAR {totals.revenue.toLocaleString()}</strong>
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
};

export default CreditBillingTable;

